import { ModalProvider } from "@/components/core/modals/modal.tsx";
import MobileUserWarning from "@/components/warnings/mobile-user-warning";
import type { AuthContextType } from "@/hooks/use-auth.tsx";
import { useLoadingBar } from "@/hooks/use-loading-bar.tsx";
import type { queryClient } from "@/query-client.ts";
import { isSupportedBrowser } from "@/utils/is-supported-browser";
import { IconAlertSquareRoundedFilled } from "@tabler/icons-react";
import { ReactQueryDevtools as TanStackReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Outlet, createRootRouteWithContext, redirect, useRouterState } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { useEffect } from "react";
import { Toaster } from "sonner";

interface RouteContext {
	queryClient: typeof queryClient;
	auth: AuthContextType;
}

function RouterLoader() {
	const isLoading = useRouterState({ select: (s) => s.status === "pending" });

	const { startLoading, completeLoading } = useLoadingBar();

	useEffect(() => {
		if (isLoading) {
			startLoading();
		} else {
			completeLoading();
		}
	}, [isLoading, startLoading, completeLoading]);

	return null;
}

export const Route = createRootRouteWithContext<RouteContext>()({
	component: RootComponent,
	beforeLoad: ({ context, location }) => {
		// Redirect root path to dashboard or login based on auth status
		if (location.pathname === "/") {
			if (context.auth?.user) {
				throw redirect({ to: "/sales-dashboard" });
				// biome-ignore lint/style/noUselessElse: <explanation>
			} else {
				throw redirect({ to: "/login" });
			}
		}
	},
	// notFoundComponent: GlobalNotFound,
});

function RootComponent() {
	// if (!isSupportedBrowser()) {
	// 	alert("Your browser is not supported. Please use the latest version of Chrome or Firefox.");
	// 	return (
	// 		<div className="flex flex-col min-h-screen w-full">
	// 			<div className="flex flex-col w-full">
	// 				<ModalProvider>
	// 					<div className="flex items-center justify-center h-screen">
	// 						<h1 className="text-2xl font-bold">Unsupported Browser </h1>
	// 						<p> Please use the latest version of Chrome or Firefox.</p>
	// 					</div>
	// 				</ModalProvider>
	// 			</div>
	// 		</div>
	// 	);
	// }
	return (
		<div className="flex flex-col min-h-screen w-full">
			<MobileUserWarning />

			<RouterLoader />

			<div className="flex flex-col w-full">
				<ModalProvider>
					<Outlet />
					<Toaster richColors />

					{import.meta.env.VITE_DEVTOOLS_ENABLED === "true" && <TanStackRouterDevtools position="bottom-right" />}
					{import.meta.env.VITE_DEVTOOLS_ENABLED === "true" && <TanStackReactQueryDevtools buttonPosition="top-right" />}
				</ModalProvider>
			</div>
		</div>
	);
}
